import * as SelectPrimitive from "@radix-ui/react-select";
import { Plus } from "lucide-react";
import * as React from "react";

import { cn } from "../../../utils";
import { Command, CommandInput, CommandList } from "../../Command";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  useIsInsideDrawer,
} from "../../Drawer";
import { PopoverContent } from "../../Popover";
import { SelectContext } from "./context";
import {
  SelectScrollDownButton,
  SelectScrollUpButton,
} from "./select-scroll-buttons";

const HiddenHydrationList = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute left-[-10000px] top-0 size-0 overflow-hidden opacity-0"
    >
      <Command shouldFilter={false}>{children}</Command>
    </div>
  );
};

const MobileSelectDrawer = ({
  open,
  onOpenChange,
  label,
  searchable,
  className,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label?: React.ReactNode;
  searchable: boolean;
  className?: string;
  children: React.ReactNode;
}) => {
  // A Select rendered inside a drawer (a field-request form, say) has to open
  // this as a NESTED drawer, or the parent stays put underneath with its own
  // overlay and scroll lock and neither sheet gives way.
  const isInsideDrawer = useIsInsideDrawer();

  return (
    <Drawer open={open} onOpenChange={onOpenChange} nested={isInsideDrawer}>
      <DrawerContent
        className={cn(
          "z-500001 text-black",
          // Searchable needs a definite height: the filtered list shrinks as the
          // user types, and a content-sized sheet would resize under them.
          searchable ? "h-[70dvh] max-h-[70dvh]" : "max-h-[70dvh]",
          className,
        )}
        overlayClassName="z-500000"
        // The Command panel owns its own scrolling, so neutralise the default
        // scroll wrapper and its padding.
        contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
        // A list of options has no single description element; opt out rather
        // than leave aria-describedby dangling.
        aria-describedby={undefined}
      >
        {/* The dialog needs an accessible name whether or not the Select was
            given a visible label. */}
        <DrawerTitle className={cn("shrink-0 px-4 pb-2", !label && "sr-only")}>
          {label ?? "Options"}
        </DrawerTitle>
        {children}
      </DrawerContent>
    </Drawer>
  );
};

type SelectContentProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Content
> & {
  label?: React.ReactNode;
};

export function SelectContent({
  className,
  children,
  position = "item-aligned",
  label: contentLabel,
  ...props
}: SelectContentProps) {
  const ctx = React.useContext(SelectContext);
  const {
    align = "start",
    side = "bottom",
    sideOffset = 4,
    ...enhancedContentProps
  } = props;

  // -------------------------------------------------------------------------
  // Simple Radix branch — item-aligned positioning
  // -------------------------------------------------------------------------
  if (ctx.isSimple) {
    return (
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className={cn(
            "relative z-500000 max-h-96 min-w-32 overflow-hidden rounded-md border bg-white text-black shadow-md",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            position === "popper" &&
              "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
            className,
          )}
          position={position}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              "p-1",
              position === "popper" &&
                "h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)",
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    );
  }

  // -------------------------------------------------------------------------
  // Enhanced branch — Popover + Command
  // -------------------------------------------------------------------------
  const {
    isMobile,
    open,
    handleOpenChange,
    searchable,
    creatable,
    search,
    setSearch,
    disabled,
    label: contextLabel,
    handleItemSelect,
    itemLabelsByValue,
  } = ctx;

  const sheetLabel = contextLabel ?? contentLabel;

  const trimmed = search.trim();
  const normalizedSearch = trimmed.toLowerCase();
  const exactMatchExists = [...itemLabelsByValue.current.entries()].some(
    ([value, label]) =>
      value.toLowerCase() === normalizedSearch ||
      label.toLowerCase() === normalizedSearch,
  );
  const showCreate = creatable && trimmed !== "" && !exactMatchExists;

  const commandPanel = (
    <Command
      className={cn(
        "bg-white text-black",
        isMobile ? "flex h-full min-h-0 rounded-none" : "rounded-md",
      )}
    >
      {searchable ? (
        <CommandInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search..."
          disabled={disabled}
          className="text-sm"
        />
      ) : null}
      <CommandList
        className={cn(
          "flex flex-col",
          isMobile
            ? "h-full max-h-none min-h-0 flex-1 p-2 pb-[env(safe-area-inset-bottom,1.5rem)]"
            : "max-h-64 p-1",
        )}
      >
        {children}
        {showCreate ? (
          <div className="order-last shrink-0">
            {/* biome-ignore lint/a11y/useSemanticElements: intentional div-button to avoid nested <button className="cursor-pointer"> inside trigger */}
            <div
              role="button"
              tabIndex={0}
              className={cn(
                "flex cursor-pointer items-center gap-2 text-black hover:bg-gray-100",
                isMobile
                  ? "px-4 py-3 text-base"
                  : "rounded-xs px-2 py-2 text-sm",
              )}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                handleItemSelect(trimmed, trimmed);
                setSearch("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleItemSelect(trimmed, trimmed);
                  setSearch("");
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              <Plus className="size-4 shrink-0 text-gray-500" />
              <span>
                Create "<strong>{trimmed}</strong>"
              </span>
            </div>
          </div>
        ) : null}
      </CommandList>
    </Command>
  );

  if (isMobile) {
    return (
      <>
        {!open ? <HiddenHydrationList>{children}</HiddenHydrationList> : null}
        <MobileSelectDrawer
          open={open}
          onOpenChange={handleOpenChange}
          label={sheetLabel}
          searchable={searchable}
          className={className}
        >
          {commandPanel}
        </MobileSelectDrawer>
      </>
    );
  }

  return (
    <>
      {!open ? <HiddenHydrationList>{children}</HiddenHydrationList> : null}
      <PopoverContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={cn(
          "z-500000 w-(--radix-popover-trigger-width) max-w-(--radix-popover-trigger-width) p-1",
          className,
        )}
        onCloseAutoFocus={(e) => e.preventDefault()}
        {...enhancedContentProps}
      >
        {commandPanel}
      </PopoverContent>
    </>
  );
}

SelectContent.displayName = SelectPrimitive.Content.displayName;
